import { useEffect, useMemo, useRef } from "react";
import { useLocale } from "react-aria";
import { LatLng, useGoogleMaps } from "@pexip-engage/utils";
import clsx from "clsx";

import { useRootLoaderData } from "~/root";
import { filterBoolean } from "~/utils/validators.utils";

interface MapProps {
  markers?: google.maps.MarkerOptions[];
  zoom?: number;
  className?: string;
  defaultLocation?: LatLng;
}

export function Map(props: MapProps) {
  const apiKey = useRootLoaderData()?.GOOGLE_MAPS_API_KEY;

  if (!apiKey) return null;

  return <InnerMap {...props} apiKey={apiKey} />;
}

function InnerMap({
  apiKey,
  className,
  defaultLocation,
  markers,
  zoom,
}: MapProps & { apiKey: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const { locale: language } = useLocale();

  const map = useGoogleMaps(ref, {
    zoom,
    language,
    apiKey,
    defaultLocation,
  });

  useEffect(() => {
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    if (!map || !markers || markers?.length === 0) {
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    const actualMarkers = markers.filter(filterBoolean);

    actualMarkers.forEach((marker) => {
      const googleMarker = new google.maps.Marker({ ...marker, map });
      markersRef.current.push(googleMarker);

      marker.position && bounds.extend(marker.position);
    });

    const hasSingleMarker = actualMarkers.length === 1;
    const position = actualMarkers[0]?.position;

    if (hasSingleMarker && typeof position?.lat === "number" && typeof position?.lng === "number") {
      map.setCenter({ lat: position.lat, lng: position.lng });
    } else {
      map.fitBounds(bounds);
    }
  }, [map, markers]);

  return <div className={clsx("w-full", className)} ref={ref} />;
}

export function useMarkers(geolocation?: string | null) {
  const markers = useMemo(() => {
    if (!geolocation) {
      return [];
    }

    const [lat, lng] = geolocation.replace(";", ",").split(",");

    if (!lat || !lng) return [];

    const marker: google.maps.MarkerOptions = {
      position: {
        lat: Number.parseFloat(lat),
        lng: Number.parseFloat(lng),
      },
    };

    return [marker];
  }, [geolocation]);

  return markers;
}
